import './ScheduleSidePanel.scss';

import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { useAppSelector as useSelector, useAppStore, useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { useFlowBuilderContext } from 'pages/FlowBuilder/context/flow-builder.context';
import SlidingSidePanel from 'components/ExperienceCanvas/SlidingSidePanel/SlidingSidePanel';
import LargeModal from 'components/Shared/LargeModal/LargeModal';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import IconSchedule from 'pages/FlowBuilder/assets/icons/IconSchedule';
import { useFeatureFlag } from 'feature-flags';
import useJourneyStatus from 'hooks/useJourneyStatus';
import TouchpointEditor from 'pages/ExperienceCanvas/TouchpointEditor/TouchpointEditor';
import { selectStepRule } from 'store/rule/rule.selector';
import { saveStepScheduleAction } from 'store/rule/rule.action';
import ScheduleForm from './ScheduleForms/ScheduleForm/ScheduleForm';
import {
  buildScheduleSavePayload,
  createEmptyDraft,
  scheduleDraftReducer,
  seedDraftFromState,
} from './ScheduleForms/scheduleDraft.helper';
import { ScheduleSidePanelDraft } from './ScheduleForms/scheduleDraft.types';

const MAIN_CLASS = 'schedule-side-panel';

function ScheduleSidePanelLegacy() {
  const flowBuilderContext = useFlowBuilderContext();
  const flowScheduleContext = flowBuilderContext.sidePanels.schedule;

  const closeSidePanel = useCallback(() => {
    flowScheduleContext.setIsOpen(false);
  }, [flowScheduleContext]);

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: flowScheduleContext.isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: <IconSchedule />,
        goBackTitle: 'Schedule',
        goBackSubTitle: 'Currently editing from Flow Builder',
        goBackActionHandler: closeSidePanel,
      },
      showFooter: false,
      size: 'large',
    }),
    [flowScheduleContext, closeSidePanel],
  );

  return (
    <div className={MAIN_CLASS}>
      <SlidingSidePanel {...slidingSidePanelProps}>
        <TouchpointEditor
          isFromFlow
          stepIdx={flowScheduleContext.stepIndex}
          activeTabIdx={0}
          overrideExitPrompt
          closeFlowSidePanel={closeSidePanel}
        />
      </SlidingSidePanel>
    </div>
  );
}

function ScheduleSidePanelStandalone() {
  const dispatch = useDispatch();
  const store = useAppStore();
  const { isJourneyLocked } = useJourneyStatus();
  const flowBuilderContext = useFlowBuilderContext();
  const flowScheduleContext = flowBuilderContext.sidePanels.schedule;
  const stepIdx = flowScheduleContext.stepIndex;
  const isModal = flowBuilderContext.shellVariant === 'modal';

  const [draft, draftDispatch] = useReducer(scheduleDraftReducer, undefined, createEmptyDraft);
  const ruleRefIdFromStore = useSelector((state) => selectStepRule(state.te, stepIdx)?.refId);
  const ruleRefId = draft.rule.refId ?? ruleRefIdFromStore;
  const [seededDraft, setSeededDraft] = useState<ScheduleSidePanelDraft>(createEmptyDraft);
  const [prevOpen, setPrevOpen] = useState(false);

  if (flowScheduleContext.isOpen !== prevOpen) {
    setPrevOpen(flowScheduleContext.isOpen);
    if (flowScheduleContext.isOpen) {
      const seeded = seedDraftFromState(store.getState(), stepIdx);
      draftDispatch({ type: 'SEED_SCHEDULE_DRAFT', payload: { draft: seeded } });
      setSeededDraft(seeded);
    }
  }

  const closeSidePanel = useCallback(() => {
    flowScheduleContext.setIsOpen(false);
  }, [flowScheduleContext]);

  const handleSecondaryAction = useCallback(() => {
    draftDispatch({ type: 'SEED_SCHEDULE_DRAFT', payload: { draft: seededDraft } });
    closeSidePanel();
  }, [closeSidePanel, seededDraft]);

  const handlePrimaryAction = useCallback(() => {
    if (isJourneyLocked) return;
    dispatch(saveStepScheduleAction(buildScheduleSavePayload(draft, stepIdx, ruleRefId)));
    closeSidePanel();
  }, [closeSidePanel, dispatch, draft, isJourneyLocked, ruleRefId, stepIdx]);

  const slidingSidePanelProps = useMemo<TSlidingSidePanelProps>(
    () => ({
      show: flowScheduleContext.isOpen,
      headerInfo: {
        mainTitle: '',
        goBackIcon: <IconSchedule />,
        goBackTitle: 'Schedule',
        goBackSubTitle: 'Currently editing from Flow Builder',
        goBackActionHandler: handleSecondaryAction,
      },
      footerInfo: {
        primaryActionText: 'Save',
        primaryActionHandler: handlePrimaryAction,
        primaryActionDisabled: isJourneyLocked,
        secondaryActionText: 'Cancel',
        secondaryActionHandler: handleSecondaryAction,
      },
      showFooter: true,
      size: 'large',
    }),
    [flowScheduleContext.isOpen, handlePrimaryAction, handleSecondaryAction, isJourneyLocked],
  );

  const formContent = (
    <ScheduleForm
      key={`schedule-slideout-form-${stepIdx}`}
      stepIdx={stepIdx}
      draft={draft}
      draftDispatch={draftDispatch}
      isJourneyLocked={isJourneyLocked}
    />
  );

  return isModal ? (
    <div className={MAIN_CLASS}>
      <LargeModal
        isOpen={flowScheduleContext.isOpen}
        closeModal={handleSecondaryAction}
        headerIcon={<IconSchedule />}
        title="Schedule"
        description="Currently editing from Flow Builder"
        showFooter={true}
        primaryAction={{
          label: 'Apply',
          onClick: handlePrimaryAction,
          disabled: isJourneyLocked,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: handleSecondaryAction,
        }}
      >
        {formContent}
      </LargeModal>
    </div>
  ) : (
    <div className={MAIN_CLASS}>
      <SlidingSidePanel {...slidingSidePanelProps}>{formContent}</SlidingSidePanel>
    </div>
  );
}

function ScheduleSidePanel() {
  const isStandaloneSlideoutsEnabled = useFeatureFlag('tx_standalone_slideouts');

  if (!isStandaloneSlideoutsEnabled) {
    return <ScheduleSidePanelLegacy />;
  }

  return <ScheduleSidePanelStandalone />;
}

export default ScheduleSidePanel;
